// Genre color system for consistent genre/subgenre visualization
// This system assigns base colors to main genres and creates variations for subgenres
// Main genre base colors - carefully selected for distinctiveness and harmony
const GENRE_BASE_COLORS = {
  Romance: {
    base: '#E11D48',
    // Rose/pink - warm, passionate
    light: '#FFF1F2',
    medium: '#FDA4AF',
    dark: '#9F1239',
    text: {
      light: '#E11D48',
      dark: '#FFF1F2',
    },
  },
  Fantasy: {
    base: '#7E22CE',
    // Purple - magical, mystical
    light: '#F3E8FF',
    medium: '#C084FC',
    dark: '#581C87',
    text: {
      light: '#7E22CE',
      dark: '#F3E8FF',
    },
  },
  'Science Fiction': {
    base: '#2563EB',
    // Blue - futuristic, technological
    light: '#EFF6FF',
    medium: '#93C5FD',
    dark: '#1E40AF',
    text: {
      light: '#2563EB',
      dark: '#EFF6FF',
    },
  },
  Mystery: {
    base: '#CA8A04',
    // Yellow/amber - intrigue, investigation
    light: '#FEF9C3',
    medium: '#FCD34D',
    dark: '#854D0E',
    text: {
      light: '#854D0E',
      dark: '#FEF9C3',
    },
  },
  Horror: {
    base: '#7F1D1D',
    // Deep red - fear, blood
    light: '#FEE2E2',
    medium: '#FECACA',
    dark: '#450A0A',
    text: {
      light: '#7F1D1D',
      dark: '#FEE2E2',
    },
  },
  Historical: {
    base: '#A16207',
    // Brown/amber - aged, vintage
    light: '#FEF3C7',
    medium: '#FCD34D',
    dark: '#78350F',
    text: {
      light: '#78350F',
      dark: '#FEF3C7',
    },
  },
  Thriller: {
    base: '#0F766E',
    // Teal - suspense, tension
    light: '#CCFBF1',
    medium: '#5EEAD4',
    dark: '#115E59',
    text: {
      light: '#0F766E',
      dark: '#CCFBF1',
    },
  },
  YoungAdult: {
    base: '#4F46E5',
    // Indigo - youth, energy
    light: '#E0E7FF',
    medium: '#A5B4FC',
    dark: '#3730A3',
    text: {
      light: '#4F46E5',
      dark: '#E0E7FF',
    },
  },
  NonFiction: {
    base: '#0369A1',
    // Sky blue - factual, informative
    light: '#E0F2FE',
    medium: '#7DD3FC',
    dark: '#075985',
    text: {
      light: '#0369A1',
      dark: '#E0F2FE',
    },
  },
  Adventure: {
    base: '#15803D',
    // Green - exploration, nature
    light: '#DCFCE7',
    medium: '#86EFAC',
    dark: '#166534',
    text: {
      light: '#15803D',
      dark: '#DCFCE7',
    },
  },
  // Fallback for unknown genres
  default: {
    base: '#525252',
    // Neutral gray
    light: '#F5F5F5',
    medium: '#A3A3A3',
    dark: '#262626',
    text: {
      light: '#525252',
      dark: '#F5F5F5',
    },
  },
}
// Subgenre modifiers based on tone/theme
const SUBGENRE_MODIFIERS = {
  // Darker themes use darker shades
  dark: 'dark',
  gothic: 'dark',
  noir: 'dark',
  grim: 'dark',
  occult: 'dark',
  horror: 'dark',
  dystopian: 'dark',
  // Lighter/cozy themes use lighter shades
  cozy: 'light',
  light: 'light',
  comedy: 'light',
  humorous: 'light',
  fairy: 'light',
  children: 'light',
  // Default is medium intensity
  default: 'medium',
}
// Helper to get the appropriate color variant based on subgenre name
function getSubgenreVariant(subgenre: string): 'light' | 'medium' | 'dark' {
  if (!subgenre) return 'medium'
  const subgenreLower = subgenre.toLowerCase()
  // Check if this is explicitly a "dark" subgenre
  if (subgenreLower.includes('dark')) {
    return 'dark'
  }
  // Check for other tone modifiers
  for (const [keyword, variant] of Object.entries(SUBGENRE_MODIFIERS)) {
    if (subgenreLower.includes(keyword)) {
      return variant as 'light' | 'medium' | 'dark'
    }
  }
  // Default to medium if no specific tone is found
  return 'medium'
}
// Get the parent genre for a subgenre
function getParentGenre(subgenre: string): string {
  // This is a simplified approach - in a real system, you'd have a mapping
  // of subgenres to their parent genres
  const subgenreLower = subgenre.toLowerCase()
  if (subgenreLower.includes('romance')) return 'Romance'
  if (subgenreLower.includes('fantasy')) return 'Fantasy'
  if (
    subgenreLower.includes('fiction') &&
    (subgenreLower.includes('science') || subgenreLower.includes('sci-fi'))
  )
    return 'Science Fiction'
  if (
    subgenreLower.includes('mystery') ||
    subgenreLower.includes('detective') ||
    subgenreLower.includes('procedural')
  )
    return 'Mystery'
  if (
    subgenreLower.includes('horror') ||
    subgenreLower.includes('occult') ||
    subgenreLower.includes('supernatural')
  )
    return 'Horror'
  if (subgenreLower.includes('historical') || subgenreLower.includes('history'))
    return 'Historical'
  if (subgenreLower.includes('thriller') || subgenreLower.includes('suspense'))
    return 'Thriller'
  if (subgenreLower.includes('ya') || subgenreLower.includes('young adult'))
    return 'YoungAdult'
  if (
    subgenreLower.includes('non-fiction') ||
    subgenreLower.includes('nonfiction') ||
    subgenreLower.includes('biography') ||
    subgenreLower.includes('memoir')
  )
    return 'NonFiction'
  if (subgenreLower.includes('adventure') || subgenreLower.includes('action'))
    return 'Adventure'
  // Default fallback
  return 'default'
}
// Main function to get the appropriate colors for a genre/subgenre combination
// genre can now be the primaryGenre or a legacy genre string
export function getGenreColors(genre: string, subgenre?: string) {
  // Normalize genre name to match our keys
  const normalizedGenre = genre
    ?.replace(/\s+/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .trim()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join('')

  // If the normalized genre doesn't exist in our base colors, try to derive it
  let finalGenre = normalizedGenre
  if (!GENRE_BASE_COLORS[normalizedGenre] && genre) {
    finalGenre = getParentGenre(genre)
  }

  // Get the base colors for this genre
  const baseColors = GENRE_BASE_COLORS[finalGenre] || GENRE_BASE_COLORS.default
  // If no subgenre, return the base colors
  if (!subgenre) {
    return {
      bg: baseColors.base,
      bgLight: baseColors.light,
      bgDark: baseColors.dark,
      text: baseColors.text.light,
      textDark: baseColors.text.dark,
    }
  }
  // Determine the appropriate variant based on the subgenre
  const variant = getSubgenreVariant(subgenre)
  // Return the appropriate color set
  return {
    bg: baseColors[variant],
    bgLight: baseColors.light,
    bgDark: baseColors.dark,
    text: variant === 'dark' ? baseColors.text.dark : baseColors.text.light,
    textDark: baseColors.text.dark,
  }
}
// Function to get Tailwind classes for a genre tag
export function getGenreTagClasses(
  genre: string,
  subgenre?: string,
  isDarkMode: boolean = false,
  badgeRadius: string = 'rounded-full',
) {
  const colors = getGenreColors(genre, subgenre)
  // Base classes for all tags — radius is now platform-aware
  let classes = `px-3 py-1 ${badgeRadius} text-sm font-medium transition-colors`
  // For dark mode, we need to handle colors differently
  if (isDarkMode) {
    // Get the appropriate background color - darker for dark mode
    if (subgenre && getSubgenreVariant(subgenre) === 'dark') {
      // For dark subgenres in dark mode, use the darkest shade with light text
      classes += ` bg-opacity-90 text-${colors.textDark}`
      // Use a custom background color with inline style instead of Tailwind classes
      // This gives us more flexibility with the exact color
      return {
        className: classes,
        style: {
          backgroundColor: colors.bgDark,
          color: colors.textDark,
        },
      }
    } else {
      // For other genres in dark mode
      classes += ` bg-opacity-80 text-${colors.textDark}`
      return {
        className: classes,
        style: {
          backgroundColor: colors.bg,
          color: colors.textDark,
        },
      }
    }
  } else {
    // Light mode
    if (subgenre && getSubgenreVariant(subgenre) === 'dark') {
      // Dark subgenres get dark background with light text
      classes += ' text-white'
      return {
        className: classes,
        style: {
          backgroundColor: colors.bgDark,
          color: '#fff',
        },
      }
    } else if (subgenre && getSubgenreVariant(subgenre) === 'light') {
      // Light subgenres get light background with dark text
      return {
        className: classes,
        style: {
          backgroundColor: colors.bgLight,
          color: colors.text,
        },
      }
    } else {
      // Medium intensity for default
      return {
        className: classes,
        style: {
          backgroundColor: colors.bg,
          color: '#fff',
        },
      }
    }
  }
}
// Function to get color for book spine based on genre
export function getBookSpineColor(genre: string) {
  const normalizedGenre = genre
    ?.replace(/\s+/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .trim()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join('')
  return (
    GENRE_BASE_COLORS[normalizedGenre]?.base || GENRE_BASE_COLORS.default.base
  )
}
export default GENRE_BASE_COLORS
